/**
 * PO ↔ Task mutual filtering (HE-F13).
 *
 * A PO is anchored to one contractor and may carry multiple tasks (or sit at
 * project level). Tasks come from each line item on GET /purchase-orders/:id.
 *
 * Re-validate the other field against the new filtered list — do not blindly
 * clear a still-valid selection. A PO is only ever released when we KNOW it does
 * not carry the task: an unloaded or project-level PO stays put, and so does one
 * the caller has flagged as not the row's to release (see `keepPo`).
 */
import type { HiredEquipmentGridRow, PurchaseOrderOption, ResourceOption, TaskOption } from '../types';
import { toTaskOption } from './poPrefill';

export type PoTaskCarry = true | false | 'unknown';

export function uniqueTasksFromPurchaseOrder(
  lines: Array<{
    taskId?: number | null;
    task?: { id: number; taskNumber?: string | null; name: string } | null;
  }>,
): TaskOption[] {
  const byId = new Map<number, TaskOption>();
  for (const line of lines) {
    const task = line.task;
    if (task) {
      if (byId.has(task.id)) continue;
      byId.set(
        task.id,
        toTaskOption({
          id: task.id,
          taskNumber: task.taskNumber,
          description: task.name,
          name: task.name,
        }),
      );
      continue;
    }
    if (line.taskId == null || byId.has(line.taskId)) continue;
    byId.set(
      line.taskId,
      toTaskOption({
        id: line.taskId,
        description: `Task ${line.taskId}`,
      }),
    );
  }
  return [...byId.values()];
}

export function uniqueTasksFromPoResources(
  resources: Array<{
    taskId?: number | null;
    taskNumber?: string | null;
    taskDescription?: string | null;
    taskLabel?: string | null;
  }>,
): TaskOption[] {
  const byId = new Map<number, TaskOption>();
  for (const resource of resources) {
    if (resource.taskId == null || byId.has(resource.taskId)) continue;
    byId.set(
      resource.taskId,
      toTaskOption({
        id: resource.taskId,
        taskNumber: resource.taskNumber,
        description: resource.taskDescription || resource.taskLabel || '',
        name: resource.taskLabel,
      }),
    );
  }
  return [...byId.values()];
}

export function uniqueTasksFromResourceOptions(resources: ResourceOption[]): TaskOption[] {
  return uniqueTasksFromPoResources(
    resources.map((resource) => ({
      taskId: resource.taskId,
      taskLabel: resource.taskLabel,
    })),
  );
}

export function mergeTaskOptions(primary: TaskOption[], extra: TaskOption[]): TaskOption[] {
  const byId = new Map<number, TaskOption>();
  for (const task of primary) byId.set(task.id, task);
  for (const task of extra) {
    if (!byId.has(task.id)) byId.set(task.id, task);
  }
  return [...byId.values()];
}

export function poCarriesTask(
  poId: number,
  taskId: number,
  tasksByPoId: Record<number, TaskOption[]>,
): PoTaskCarry {
  const tasks = tasksByPoId[poId];
  if (!tasks) return 'unknown';
  // PO loaded with no line-level tasks — do not constrain task/PO mutual filtering.
  if (tasks.length === 0) return 'unknown';
  return tasks.some((task) => task.id === taskId);
}

/**
 * Task picker scope for a PO-backed grid row.
 * Returns `undefined` when the PO is still loading or carries no tasks so the
 * user can search all active tasks instead of seeing a dead-end empty list.
 */
export function scopedTasksForPoRow(
  row: HiredEquipmentGridRow,
  tasksByPoId: Record<number, TaskOption[]>,
  resourceOptions: ResourceOption[],
  selectedTask: TaskOption | null,
): TaskOption[] | undefined {
  if (row.poId == null) return undefined;
  const poTasks = tasksByPoId[row.poId];
  if (poTasks === undefined) return undefined;

  let merged = mergeTaskOptions(poTasks, uniqueTasksFromResourceOptions(resourceOptions));
  if (selectedTask) {
    merged = mergeTaskOptions(merged, [selectedTask]);
  }
  return merged.length > 0 ? merged : undefined;
}

/**
 * POs for a vendor, narrowed to those that carry `taskId` when it is set.
 * POs whose task list has not loaded yet stay visible (fail open) so we never
 * hide a valid PO while lookups are in flight.
 */
export function filterPosForTask(
  pos: PurchaseOrderOption[],
  taskId: number | null | undefined,
  tasksByPoId: Record<number, TaskOption[]>,
): PurchaseOrderOption[] {
  if (taskId == null) return pos;
  return pos.filter((po) => poCarriesTask(po.id, taskId, tasksByPoId) !== false);
}

export function taskMatchesQuery(task: TaskOption, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [task.taskNumber, task.description, task.name, task.projectName, String(task.id)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function filterTasksByQuery(tasks: TaskOption[], query: string): TaskOption[] {
  const q = query.trim();
  if (!q) return tasks;
  return tasks.filter((task) => taskMatchesQuery(task, q));
}

export function nextRowAfterTaskChange(
  row: HiredEquipmentGridRow,
  task: TaskOption | null,
  tasksByPoId: Record<number, TaskOption[]>,
  formatLabel: (task: TaskOption) => string,
  keepPo: boolean = false,
): Partial<HiredEquipmentGridRow> {
  if (!task) {
    return { taskId: null, taskLabel: null, taskCreatedInSession: false, cloneTaskOnSubmit: false };
  }
  const updates: Partial<HiredEquipmentGridRow> = {
    taskId: task.id,
    taskLabel: formatLabel(task),
    // Picking a different task hands the row to one the submission does not own. Callers that DO
    // own the new task (inline create, duplicate-clone) set the flag back on afterwards.
    taskCreatedInSession: false,
    // A different task was picked, so there is no duplicated task left to clone (V2-011).
    cloneTaskOnSubmit: false,
  };
  // `keepPo` mirrors `taskLocked` on nextRowAfterPoChange: the caller is telling us the PO is not
  // this row's to release. Set it when the submission is anchored to a PO (the row has no PO picker
  // to refill from), or when the task was just created and so cannot be on any PO's line items yet.
  if (!keepPo && row.poId != null && poCarriesTask(row.poId, task.id, tasksByPoId) === false) {
    updates.poId = null;
    updates.poNumber = null;
    updates.resourceId = null;
    updates.resourceLabel = null;
  }
  return updates;
}

export function nextRowAfterPoChange(
  row: HiredEquipmentGridRow,
  po: PurchaseOrderOption | null,
  tasksByPoId: Record<number, TaskOption[]>,
  taskLocked: boolean,
): Partial<HiredEquipmentGridRow> {
  if (!po) {
    return { poId: null, poNumber: null, resourceId: null, resourceLabel: null };
  }
  const updates: Partial<HiredEquipmentGridRow> = {
    poId: po.id,
    poNumber: po.poNumber,
    resourceId: null,
    resourceLabel: null,
  };
  if (!taskLocked && row.taskId != null && poCarriesTask(po.id, row.taskId, tasksByPoId) === false) {
    updates.taskId = null;
    updates.taskLabel = null;
  }
  return updates;
}
